import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { get, post, del } from '../lib/api';
import TopBar from '../components/layout/TopBar';
import SectionCard from '../components/widgets/SectionCard';
import KpiCard from '../components/widgets/KpiCard';
import EmptyState from '../components/widgets/EmptyState';
import { fmtBRL, fmtDate, todayISO } from '../lib/format';
import { Repeat, Plus, Trash2, TrendingUp, DollarSign, AlertCircle, FileCheck, RefreshCw, XCircle, Award } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const STATUS_PILL = {
  ATIVO: 'pill-success',
  ENCERRADO: 'pill-muted',
  INADIMPLENTE: 'pill-danger',
  CANCELADO: 'pill-warning',
  RENOVADO: 'pill-muted',
  PERDIDO: 'pill-danger',
};

const blank = () => ({
  company_id: '',
  client_name: '',
  contract_code: '',
  description: '',
  monthly_value: '',
  installments: 12,
  payment_type: 'RECORRENTE',
  start_date: todayISO(),
  payment_start_date: '',
  due_day: 10,
  plan: '',
  status: 'ATIVO',
  notes: '',
  generate_receivables: true,
});

const blankRenew = () => ({
  monthly_value: '',
  installments: 12,
  payment_type: '',
  payment_start_date: todayISO(),
  due_day: '',
  plan: '',
  notes: '',
});

export default function Contratos() {
  const { companies, companyFilter } = useApp();
  const [list, setList] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());

  const [renewOpen, setRenewOpen] = useState(false);
  const [renewTarget, setRenewTarget] = useState(null);
  const [renewForm, setRenewForm] = useState(blankRenew());

  const [lossOpen, setLossOpen] = useState(false);
  const [lossTarget, setLossTarget] = useState(null);
  const [lossReason, setLossReason] = useState('');

  const load = async () => {
    const cid = companyFilter === 'all' ? null : companyFilter;
    const [c, k] = await Promise.all([
      get('/contracts', cid ? { company_id: cid } : {}),
      get('/contracts/summary/kpis', cid ? { company_id: cid } : {}),
    ]);
    setList(c);
    setKpis(k);
  };
  useEffect(() => { load(); }, [companyFilter]);

  const isAVista = form.payment_type === 'A_VISTA';

  const submit = async () => {
    if (!form.company_id || !form.client_name.trim()) return toast.error('Empresa e cliente obrigatórios');
    const mv = parseFloat(String(form.monthly_value).replace(',', '.'));
    if (!mv || mv <= 0) return toast.error('Valor inválido');
    const inst = isAVista ? 1 : parseInt(form.installments, 10);
    if (!isAVista && (!inst || inst <= 0)) return toast.error('Quantidade de parcelas inválida');
    if (isAVista && !form.payment_start_date) return toast.error('Informe a data de vencimento à vista');

    try {
      await post('/contracts', {
        ...form,
        monthly_value: mv,
        installments: isAVista ? 1 : inst,
        due_day: parseInt(form.due_day, 10) || 10,
        contract_code: form.contract_code || null,
        description: form.description || null,
        notes: form.notes || null,
        payment_start_date: form.payment_start_date || null,
        plan: form.plan || null,
      });
      const msg = isAVista
        ? 'Contrato à vista criado! 1 recebível gerado.'
        : `Contrato criado! ${inst} parcelas geradas como Contas a Receber.`;
      toast.success(msg);
      setOpen(false); setForm(blank()); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    }
  };

  const remove = async (cid, deleteReceivables) => {
    const msg = deleteReceivables
      ? 'EXCLUIR contrato + TODAS as parcelas (recebíveis gerados)?'
      : 'Excluir apenas o contrato?';
    if (!window.confirm(msg)) return;
    await del(`/contracts/${cid}?delete_receivables=${deleteReceivables}`);
    toast.success('Excluído');
    load();
  };

  const openRenew = (c) => {
    setRenewTarget(c);
    setRenewForm({
      ...blankRenew(),
      monthly_value: String(c.monthly_value).replace('.', ','),
      installments: c.payment_type === 'A_VISTA' ? 1 : (c.installments || 12),
      payment_type: c.payment_type || 'RECORRENTE',
      due_day: c.due_day || 10,
      plan: c.plan || '',
    });
    setRenewOpen(true);
  };

  const submitRenew = async () => {
    if (!renewForm.payment_start_date) return toast.error('Informe a data de início do pagamento');
    const mv = renewForm.monthly_value
      ? parseFloat(String(renewForm.monthly_value).replace(',', '.'))
      : null;
    if (mv !== null && (isNaN(mv) || mv <= 0)) return toast.error('Valor inválido (deixe em branco para manter o atual)');
    const inst = parseInt(renewForm.installments, 10) || 12;
    try {
      await post(`/contracts/${renewTarget.id}/renew`, {
        monthly_value: mv,
        installments: renewForm.payment_type === 'A_VISTA' ? 1 : inst,
        payment_type: renewForm.payment_type || null,
        payment_start_date: renewForm.payment_start_date,
        due_day: renewForm.due_day ? parseInt(renewForm.due_day, 10) : null,
        plan: renewForm.plan || null,
        notes: renewForm.notes || null,
      });
      toast.success(`Contrato renovado! Novo período de ${renewForm.payment_type === 'A_VISTA' ? '1 pagamento' : `${inst} meses`}.`);
      setRenewOpen(false);
      setRenewTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao renovar');
    }
  };

  const openLoss = (c) => {
    setLossTarget(c);
    setLossReason('');
    setLossOpen(true);
  };

  const submitLoss = async () => {
    if (!lossReason.trim()) return toast.error('Informe o motivo da perda');
    try {
      await post(`/contracts/${lossTarget.id}/loss`, { lost_reason: lossReason.trim() });
      toast.success('Contrato marcado como PERDIDO. Recebíveis pendentes apagados.');
      setLossOpen(false);
      setLossTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    }
  };

  return (
    <div className="fade-in">
      <TopBar
        title="Contratos Recorrentes"
        subtitle="Recorrente ou à vista, com geração automática de recebíveis. Renovação e perda rastreáveis."
        action={
          <Button onClick={() => { setForm(blank()); setOpen(true); }} className="btn-gold rounded-md gap-1.5 px-4" data-testid="btn-new-contract">
            <Plus size={15} /> Novo Contrato
          </Button>
        }
      />
      <div className="px-8 py-7 space-y-5">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard label="MRR (Recorrente)" value={kpis?.mrr || 0} accent="#D4AF37" icon={Repeat} testid="kpi-mrr" highlight />
          <KpiCard label="ARR (Anual)" value={kpis?.arr || 0} accent="#10B981" icon={TrendingUp} testid="kpi-arr" />
          <KpiCard label="Contratado Total" value={kpis?.contracted_total || 0} accent="#3B82F6" icon={DollarSign} testid="kpi-contracted" />
          <KpiCard label="Ativos" value={kpis?.active_contracts || 0} format="number" accent="#8B5CF6" icon={FileCheck} testid="kpi-active" />
        </div>

        <SectionCard padded={false} testid="contracts-table">
          {list.length === 0 ? (
            <div className="p-12">
              <EmptyState
                icon={Repeat}
                title="Nenhum contrato cadastrado"
                description="Cadastre contratos recorrentes (mensais) ou à vista. O sistema gera recebíveis automaticamente."
                action={<Button onClick={() => setOpen(true)} className="btn-gold">Criar Contrato</Button>}
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full ceap-table">
                <thead>
                  <tr>
                    <th>Cliente / Código</th>
                    <th>Empresa</th>
                    <th>Tipo</th>
                    <th className="text-right">Valor</th>
                    <th className="text-center">Parcelas</th>
                    <th className="text-right">Total</th>
                    <th>Início Pgto</th>
                    <th className="text-center">Status</th>
                    <th className="text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((c) => {
                    const co = companies.find((x) => x.id === c.company_id);
                    const isVista = c.payment_type === 'A_VISTA';
                    const installments = isVista ? 1 : (c.installments || 1);
                    const total = c.monthly_value * installments;
                    const canRenew = c.status === 'ATIVO' || c.status === 'ENCERRADO' || c.status === 'INADIMPLENTE';
                    const canLoss = c.status === 'ATIVO' || c.status === 'INADIMPLENTE';
                    return (
                      <tr key={c.id} data-testid={`contract-row-${c.id}`}>
                        <td>
                          <div className="text-zinc-100 font-medium flex items-center gap-1.5">
                            {c.client_name}
                            {c.is_renewal && (
                              <span className="inline-flex items-center gap-1 text-[9px] text-amber-400 bg-amber-950/30 border border-amber-700/40 px-1.5 py-0.5 rounded-sm uppercase tracking-wider" title={`Renovação${c.renewal_count ? ` (${c.renewal_count}ª)` : ''}`}>
                                <RefreshCw size={9} /> Renovação{c.renewal_count > 1 ? ` ${c.renewal_count}ª` : ''}
                              </span>
                            )}
                          </div>
                          {c.contract_code && <div className="text-[11px] text-zinc-500 font-mono">{c.contract_code}</div>}
                          {c.lost_reason && <div className="text-[10px] text-red-400/80 italic mt-0.5">Motivo perda: {c.lost_reason}</div>}
                        </td>
                        <td>
                          <div className="flex items-center gap-1.5">
                            <span className="w-1.5 h-4 rounded-sm" style={{ background: co?.color || '#888' }} />
                            <span className="text-zinc-300 text-xs">{co?.name || '-'}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`text-[10px] pill ${isVista ? 'pill-warning' : 'pill-info'}`}>
                            {isVista ? 'À VISTA' : 'RECORRENTE'}
                          </span>
                        </td>
                        <td className="text-right font-mono gold-text font-semibold">{fmtBRL(c.monthly_value)}</td>
                        <td className="text-center font-mono text-zinc-300">{isVista ? '—' : `${installments}x`}</td>
                        <td className="text-right font-mono text-zinc-100">{fmtBRL(total)}</td>
                        <td className="font-mono text-zinc-400 text-xs">{fmtDate(c.payment_start_date || c.start_date)}</td>
                        <td className="text-center">
                          <span className={`pill ${STATUS_PILL[c.status] || 'pill-muted'}`}>{c.status}</span>
                        </td>
                        <td className="text-right">
                          <div className="flex justify-end gap-1">
                            {canRenew && (
                              <button onClick={() => openRenew(c)} className="p-1.5 rounded hover:bg-amber-900/30 text-zinc-400 hover:text-amber-400" title="Renovar contrato" data-testid={`btn-renew-${c.id}`}>
                                <RefreshCw size={13} />
                              </button>
                            )}
                            {canLoss && (
                              <button onClick={() => openLoss(c)} className="p-1.5 rounded hover:bg-red-900/30 text-zinc-400 hover:text-orange-400" title="Marcar como perdido" data-testid={`btn-loss-${c.id}`}>
                                <XCircle size={13} />
                              </button>
                            )}
                            <button onClick={() => remove(c.id, true)} className="p-1.5 rounded hover:bg-red-900/30 text-zinc-400 hover:text-red-400" title="Excluir contrato + parcelas" data-testid={`btn-delete-contract-${c.id}`}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>

      {/* DIALOG novo contrato */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="dialog-contract">
          <DialogHeader>
            <DialogTitle className="font-display">Novo Contrato</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Recorrente: parcelas mensais. À vista: 1 único pagamento na data de vencimento. Recebíveis criados automaticamente.
            </DialogDescription>
          </DialogHeader>

          {/* Toggle tipo de pagamento */}
          <div className="grid grid-cols-2 gap-2 mb-3" data-testid="payment-type-toggle">
            <button
              type="button"
              onClick={() => setForm({ ...form, payment_type: 'RECORRENTE' })}
              className={`p-3 rounded-md border transition-all ${form.payment_type === 'RECORRENTE' ? 'border-amber-500/60 bg-amber-950/30 text-amber-300' : 'border-zinc-800 bg-zinc-900/40 text-zinc-500 hover:text-zinc-300'}`}
              data-testid="btn-payment-recorrente"
            >
              <div className="text-xs uppercase tracking-wider font-semibold">Recorrente</div>
              <div className="text-[11px] mt-0.5 opacity-80">Parcelas mensais (qualquer quantidade)</div>
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, payment_type: 'A_VISTA', installments: 1 })}
              className={`p-3 rounded-md border transition-all ${form.payment_type === 'A_VISTA' ? 'border-blue-500/60 bg-blue-950/30 text-blue-300' : 'border-zinc-800 bg-zinc-900/40 text-zinc-500 hover:text-zinc-300'}`}
              data-testid="btn-payment-avista"
            >
              <div className="text-xs uppercase tracking-wider font-semibold">À Vista</div>
              <div className="text-[11px] mt-0.5 opacity-80">Pagamento único em data específica</div>
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Empresa *</Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 mt-1" data-testid="contract-company"><SelectValue placeholder="Empresa" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Cliente *</Label>
              <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" placeholder="Ex: Pref. de Florianópolis" data-testid="contract-client" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Código do Contrato</Label>
              <Input value={form.contract_code} onChange={(e) => setForm({ ...form, contract_code: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1 font-mono" placeholder="Opcional" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">{isAVista ? 'Valor Total (R$) *' : 'Valor Mensal (R$) *'}</Label>
              <Input value={form.monthly_value} onChange={(e) => setForm({ ...form, monthly_value: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1 font-mono" placeholder={isAVista ? '12000,00' : '1500,00'} data-testid="contract-monthly" />
            </div>
            {!isAVista && (
              <>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Qtd Parcelas (meses) *</Label>
                  <Input
                    type="number"
                    min={1}
                    max={120}
                    value={form.installments}
                    onChange={(e) => setForm({ ...form, installments: e.target.value })}
                    className="bg-zinc-900 border-zinc-800 mt-1 font-mono"
                    data-testid="contract-installments"
                    placeholder="Ex: 4, 10, 24..."
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Dia de Vencimento</Label>
                  <Input type="number" min={1} max={28} value={form.due_day} onChange={(e) => setForm({ ...form, due_day: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1 font-mono" data-testid="contract-due-day" />
                </div>
              </>
            )}
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Data Início Contrato *</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1 font-mono" data-testid="contract-start" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                {isAVista ? 'Data de Vencimento (à vista) *' : 'Início do Pagamento'}
              </Label>
              <Input type="date" value={form.payment_start_date} onChange={(e) => setForm({ ...form, payment_start_date: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1 font-mono" data-testid="contract-payment-start" />
              <p className="text-[10px] text-zinc-500 mt-1">{isAVista ? 'Quando o pagamento será efetivado' : 'Se 1ª parcela for em mês diferente do início'}</p>
            </div>
            {(() => {
              const co = companies.find((x) => x.id === form.company_id);
              const isLicito = co && co.name && co.name.toLowerCase().includes('lícito');
              if (!isLicito) return null;
              return (
                <div className="col-span-2">
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Plano (Lícito.Guru)</Label>
                  <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                    <SelectTrigger className="bg-zinc-900 border-zinc-800 mt-1" data-testid="contract-plan"><SelectValue placeholder="Selecione o plano" /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                      <SelectItem value="PERSONALIZADO">Personalizado</SelectItem>
                      <SelectItem value="PREMIUM">Premium</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              );
            })()}
            <div className="col-span-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Descrição / Notas</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" rows={2} />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <input
                type="checkbox" id="genrec"
                checked={form.generate_receivables}
                onChange={(e) => setForm({ ...form, generate_receivables: e.target.checked })}
                className="rounded border-zinc-700"
                data-testid="contract-generate-recv"
              />
              <label htmlFor="genrec" className="text-sm text-zinc-300 cursor-pointer">
                Gerar automaticamente {isAVista ? '1 recebível' : `as ${form.installments || 0} parcelas`} como Contas a Receber
              </label>
            </div>
            {form.monthly_value && (
              <div className="col-span-2 bg-amber-950/20 border border-amber-700/30 rounded-md p-3">
                <div className="text-xs text-amber-300 font-medium mb-1">Resumo</div>
                <div className="text-sm text-zinc-200 font-mono">
                  {isAVista ? (
                    <>1x à vista: <span className="gold-text font-bold">{fmtBRL(parseFloat(String(form.monthly_value).replace(',', '.')) || 0)}</span></>
                  ) : (
                    <>{form.installments}x {fmtBRL(parseFloat(String(form.monthly_value).replace(',', '.')) || 0)} = <span className="gold-text font-bold">{fmtBRL((parseFloat(String(form.monthly_value).replace(',', '.')) || 0) * form.installments)}</span></>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-zinc-400 hover:bg-zinc-900">Cancelar</Button>
            <Button onClick={submit} className="btn-gold" data-testid="btn-save-contract">Criar Contrato</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG renovação */}
      <Dialog open={renewOpen} onOpenChange={setRenewOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-xl" data-testid="dialog-renew">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <RefreshCw size={17} className="text-amber-400" />
              Renovar Contrato
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              {renewTarget?.client_name} · O contrato atual será marcado como RENOVADO (não conta mais nos ativos) e um novo será criado como RENOVAÇÃO.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-2 mb-2">
            <button
              type="button"
              onClick={() => setRenewForm({ ...renewForm, payment_type: 'RECORRENTE' })}
              className={`p-2.5 rounded-md border transition-all ${renewForm.payment_type === 'RECORRENTE' ? 'border-amber-500/60 bg-amber-950/30 text-amber-300' : 'border-zinc-800 bg-zinc-900/40 text-zinc-500'}`}
            >
              <div className="text-xs uppercase tracking-wider font-semibold">Recorrente</div>
            </button>
            <button
              type="button"
              onClick={() => setRenewForm({ ...renewForm, payment_type: 'A_VISTA', installments: 1 })}
              className={`p-2.5 rounded-md border transition-all ${renewForm.payment_type === 'A_VISTA' ? 'border-blue-500/60 bg-blue-950/30 text-blue-300' : 'border-zinc-800 bg-zinc-900/40 text-zinc-500'}`}
            >
              <div className="text-xs uppercase tracking-wider font-semibold">À Vista</div>
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">{renewForm.payment_type === 'A_VISTA' ? 'Valor Total (R$)' : 'Valor Mensal (R$)'}</Label>
              <Input value={renewForm.monthly_value} onChange={(e) => setRenewForm({ ...renewForm, monthly_value: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1 font-mono" placeholder="Em branco mantém atual" data-testid="renew-value" />
            </div>
            {renewForm.payment_type !== 'A_VISTA' && (
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Novo Período (meses)</Label>
                <Input
                  type="number"
                  min={1}
                  max={120}
                  value={renewForm.installments}
                  onChange={(e) => setRenewForm({ ...renewForm, installments: e.target.value })}
                  className="bg-zinc-900 border-zinc-800 mt-1 font-mono"
                  data-testid="renew-installments"
                  placeholder="Ex: 4, 10, 24..."
                />
              </div>
            )}
            <div className="col-span-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                {renewForm.payment_type === 'A_VISTA' ? 'Data do Pagamento *' : 'Data 1ª Parcela da Renovação *'}
              </Label>
              <Input type="date" value={renewForm.payment_start_date} onChange={(e) => setRenewForm({ ...renewForm, payment_start_date: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1 font-mono" data-testid="renew-payment-start" />
            </div>
            <div className="col-span-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Observações</Label>
              <Textarea value={renewForm.notes} onChange={(e) => setRenewForm({ ...renewForm, notes: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" rows={2} placeholder="Ex: cliente aceitou reajuste de 10%" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenewOpen(false)} className="text-zinc-400 hover:bg-zinc-900">Cancelar</Button>
            <Button onClick={submitRenew} className="btn-gold gap-1.5" data-testid="btn-confirm-renew">
              <RefreshCw size={13} /> Confirmar Renovação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG perda */}
      <Dialog open={lossOpen} onOpenChange={setLossOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md" data-testid="dialog-loss">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-red-400">
              <XCircle size={17} />
              Marcar como Perdido
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              {lossTarget?.client_name} · O contrato sairá dos ATIVOS. Recebíveis pendentes serão removidos.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Motivo da Perda *</Label>
            <Textarea value={lossReason} onChange={(e) => setLossReason(e.target.value)} className="bg-zinc-900 border-zinc-800 mt-1" rows={3} placeholder="Ex: cliente trocou de fornecedor, problema financeiro, etc" data-testid="loss-reason" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLossOpen(false)} className="text-zinc-400 hover:bg-zinc-900">Cancelar</Button>
            <Button onClick={submitLoss} className="bg-red-700 hover:bg-red-600 text-white gap-1.5" data-testid="btn-confirm-loss">
              <XCircle size={13} /> Confirmar Perda
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
